import LayoutContainer from 'components/Common/LayoutContainer'
import Sheet from 'components/utils/Sheet'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import LoyaltyAccountLandingPageBenefitItem from './LoyaltyAccountLandingPageBenefitItem'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { useLocation } from 'react-router-dom'
import getIsLoyaltyEsimEnabled from 'loyalty/selectors/getIsLoyaltyEsimEnabled'
import { sortBy } from 'lib/array/arrayUtils'
import Group from 'components/utils/Group'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Clickable from 'components/Common/Clickable'
import ModalContext from 'contexts/ModalContext'
import LoyaltyMembershipYearModal from 'loyalty/components/LoyaltyMembershipYearModal'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import getShouldHideLoyaltyStatusCreditsAndNights from 'loyalty/selectors/getShouldHideLoyaltyStatusCreditsAndNights'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'

const Section = styled(Sheet)`
  /* Offset for sticky header */
  scroll-margin-top: 80px;
`

/** Show the first N benefits as tiles only when there are more benefits to list below. */
const FEATURED_TILE_COUNT = 3

/** Display order matching the design. Codes not listed here are not rendered. */
const BENEFIT_DISPLAY_ORDER: Array<App.LoyaltyBenefitType> = [
  'hotel_upgrade',
  'lounge_pass',
  'airport_transfer',
  'esim_package',
  'tour_exp_credit',
  'travelshoot_credit',
  'discounted_insurance',
  'phone_support',
]

/**
 * Display order for Infinite members (matches the Infinite design): surfaces the exclusive-savings
 * row after the three featured tiles, moves eSIM data to the end and drops phone support.
 */
const INFINITE_BENEFIT_DISPLAY_ORDER: Array<App.LoyaltyBenefitType> = [
  'hotel_upgrade',
  'lounge_pass',
  'airport_transfer',
  'family_and_friends_access',
  'tour_exp_credit',
  'travelshoot_credit',
  'discounted_insurance',
  'esim_package',
]

const TilesGrid = styled.div`
  display: grid;
  gap: ${rem(8)};
  grid-template-columns: 1fr;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(${FEATURED_TILE_COUNT}, 1fr);
  }
`

function LoyaltyAccountLandingPageYourBenefits() {
  const intl = useIntl()
  const location = useLocation()
  const account = useAppSelector(state => state.loyalty.account.data)
  const isEsimEnabled = useAppSelector(getIsLoyaltyEsimEnabled)
  const isInfiniteTier = useAppSelector(getLoyaltyIsInfiniteAccountTier)
  const shouldHideStatusCreditsAndNights = useAppSelector(getShouldHideLoyaltyStatusCreditsAndNights)
  const [, tierName] = useCurrentLoyaltyTier()
  const [config] = useLoyaltyConfig()
  const currentRegionCode = useAppSelector(state => state.geo.currentRegionCode)
  const isCurrentRegionLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const showModal = useContext(ModalContext)

  const displayOrder = isInfiniteTier ? INFINITE_BENEFIT_DISPLAY_ORDER : BENEFIT_DISPLAY_ORDER

  const handleMembershipYearModal = useCallback(() => {
    if (account) {
      showModal(<LoyaltyMembershipYearModal account={account} />)
    }
  }, [showModal, account])

  const visibleBenefits = useMemo(() => {
    if (!account?.benefits) return []
    const renderableBenefits = account.benefits.filter(benefit => {
      if (benefit.code === 'esim_package' && !isEsimEnabled) return false
      return displayOrder.includes(benefit.code)
    })
    const sorted = sortBy(renderableBenefits, benefit => displayOrder.indexOf(benefit.code), 'asc')

    // Amounts from account/me are the member's home-region values. When a member views another
    // region, show that region's configured amounts for their tier instead. Only the displayed
    // value changes — remaining/limit (their actual entitlement) still comes from the account.
    const tierRegionBenefits = config?.region === currentRegionCode ? config.regionTierBenefits[account.tier] : undefined
    if (isCurrentRegionLoyaltyRegion || !tierRegionBenefits) return sorted

    const regionBenefitsByCode = new Map(tierRegionBenefits.map(benefit => [benefit.code, benefit]))
    return sorted.map(benefit => {
      const regionValue = regionBenefitsByCode.get(benefit.code)?.value
      return regionValue ? { ...benefit, value: regionValue } : benefit
    })
  }, [account?.benefits, account?.tier, isEsimEnabled, displayOrder, config, currentRegionCode, isCurrentRegionLoyaltyRegion])

  useEffect(() => {
    if (location.hash === '#benefits-your-tier') {
      document.getElementById('benefits-your-tier')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [location.hash])

  if (visibleBenefits.length === 0) return null

  // Feature the first few benefits as tiles, but only when there are more to list below.
  const showTiles = visibleBenefits.length > FEATURED_TILE_COUNT
  const tileBenefits = showTiles ? visibleBenefits.slice(0, FEATURED_TILE_COUNT) : []
  const rowBenefits = showTiles ? visibleBenefits.slice(FEATURED_TILE_COUNT) : visibleBenefits

  return (
    <Section id="benefits-your-tier" background="normal-background" padding="24 0" tabletPadding="48 0">
      <LayoutContainer variant="medium">
        <VerticalSpacer gap={20}>
          <Group direction="vertical" gap={4}>
            <Heading variant="heading3">
              {isInfiniteTier && <FormattedMessage
                defaultMessage="Your exclusive benefits"
                id="17KVqo8YZ7Ox0Gq2euYG"
                description="Heading for the Infinite tier member's exclusive benefits section on the loyalty account page"
              />}
              {!isInfiniteTier && <FormattedMessage
                defaultMessage="Your {tierName} benefits"
                id="GesANU7hZxCme7SLqhSa"
                description="Heading for user's current loyalty tier benefits section"
                values={{ tierName }}
              />}
            </Heading>
            {!shouldHideStatusCreditsAndNights && !!account?.statusReviewDate && <Group direction="horizontal" gap={4} verticalAlign="center">
              <BodyText variant="medium">
                <FormattedMessage
                  id="NA3WzkfvTsEuFv-49rdB"
                  defaultMessage="Expire {date}"
                  description="Expiry date for loyalty tier benefits in the loyalty program section"
                  values={{ date: <FormatDateTime value={account.statusReviewDate} format="dmy-casual" /> }}
                />
              </BodyText>
              <Clickable onClick={handleMembershipYearModal} aria-label={intl.formatMessage({ id: 'FBbkvtnX2AxDJtQN-9yO', defaultMessage: 'Learn how membership years work', description: 'Accessible label for the info icon that opens the membership year explanation modal' })}>
                <LineInfoCircleIcon size={16} />
              </Clickable>
            </Group>}
          </Group>
          <VerticalSpacer gap={8}>
            {tileBenefits.length > 0 && <TilesGrid>
              {tileBenefits.map(benefit => <LoyaltyAccountLandingPageBenefitItem
                key={benefit.code}
                layout="tile"
                benefit={benefit}
              />)}
            </TilesGrid>}
            {rowBenefits.map(benefit => <LoyaltyAccountLandingPageBenefitItem
              key={benefit.code}
              layout="row"
              benefit={benefit}
            />)}
          </VerticalSpacer>
        </VerticalSpacer>
      </LayoutContainer>
    </Section>
  )
}

export default LoyaltyAccountLandingPageYourBenefits
